import { DiagramComponent } from '../../core/DiagramComponent';
import type { DropBinConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

/**
 * DropBin Component - A container that can receive draggable dots
 */
class DropBin extends DiagramComponent<DropBinConfig> {
  binId: string;
  width: number;
  height: number;
  center: [number, number];

  constructor(config: DropBinConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
    this.binId = config.binId;
    this.width = config.width || 3;
    this.height = config.height || 3;
    this.center = [config.position[0], config.position[1]];
  }

  render(): JXGElement[] {
    const x = Number(this.evaluateExpression(this.config.position[0]));
    const y = Number(this.evaluateExpression(this.config.position[1]));
    this.center = [x, y];
    
    const color = this.config.color || '#3498db';
    const label = this.config.label || '';

    const left = x - this.width / 2;
    const right = x + this.width / 2;
    const top = y + this.height / 2;
    const bottom = y - this.height / 2;

    // Create the four corners of the rectangle
    const p1 = this.board.create('point', [left, top], {
      visible: false,
      withLabel: false,
      name: ''
    });
    const p2 = this.board.create('point', [right, top], {
      visible: false,
      withLabel: false,
      name: ''
    });
    const p3 = this.board.create('point', [right, bottom], {
      visible: false,
      withLabel: false,
      name: ''
    });
    const p4 = this.board.create('point', [left, bottom], {
      visible: false,
      withLabel: false,
      name: ''
    });

    // Create the rectangle polygon
    const rectangle = this.board.create('polygon', [p1, p2, p3, p4], {
      strokeColor: color,
      strokeWidth: 3,
      fillColor: color,
      fillOpacity: 0.1,
      vertices: {
        visible: false,
        withLabel: false,
        name: ''
      },
      borders: {
        strokeColor: color,
        strokeWidth: 3,
      }
    });

    this.elements.push(p1, p2, p3, p4, rectangle);

    // Add label below bin
    if (label) {
      const labelText = this.board.create('text', [x, bottom - 0.5, label], {
        fontSize: 16,
        anchorX: 'middle',
        anchorY: 'top',
        fixed: true,
        cssClass: 'jxg-text-no-pointer',
      });
      this.elements.push(labelText);
    }

    return this.elements;
  }

  /** Check if a point is inside this bin */
  containsPoint(px: number, py: number): boolean {
    const [x, y] = this.center;
    const halfW = this.width / 2;
    const halfH = this.height / 2;
    
    return px >= x - halfW && px <= x + halfW && 
           py >= y - halfH && py <= y + halfH;
  }

  /** Get a position for placing a dot at given index */
  getDotPosition(index: number, totalDots: number): [number, number] {
    const [cx, cy] = this.center;
    const margin = 0.4;
    const availableWidth = this.width - margin * 2;
    const availableHeight = this.height - margin * 2;
    
    // Arrange in a grid pattern
    const cols = Math.ceil(Math.sqrt(totalDots));
    const rows = Math.ceil(totalDots / cols);
    
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    const spacingX = cols > 1 ? availableWidth / (cols - 1) : 0;
    const spacingY = rows > 1 ? availableHeight / (rows - 1) : 0;
    
    const startX = cx - availableWidth / 2;
    const startY = cy + availableHeight / 2;
    
    const dotX = cols > 1 ? startX + col * spacingX : cx;
    const dotY = rows > 1 ? startY - row * spacingY : cy;
    
    return [dotX, dotY];
  }

  /** Get bin bounds */
  getBounds(): { left: number; right: number; top: number; bottom: number } {
    const [x, y] = this.center;
    return {
      left: x - this.width / 2,
      right: x + this.width / 2,
      top: y + this.height / 2,
      bottom: y - this.height / 2,
    };
  }

  update(variables: Variables): void {
    this.parentState = { ...this.parentState, ...variables };
  }
}

export default DropBin;
